import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Trash2 } from 'lucide-react';
import { MarkdownEditor } from '@houseops/markdown-editor';
import '@houseops/markdown-editor/styles.css';
import {
  useGuide,
  useDomains,
  usePatchGuide,
  useDeleteGuide,
} from '@/lib/queries';
import { useAuth } from '@/lib/auth-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SlugField, { type SlugStatus } from '@/components/SlugField';
import ConfirmDialog from '@/components/ConfirmDialog';

/**
 * Edit page for an existing guide. Mirrors ChecklistTemplateEdit's
 * structure: hydration guards, hydrated-flag pattern, SlugField with
 * `initialEdited`, sticky action bar, danger zone.
 */
export default function GuideEdit() {
  const { idOrSlug } = useParams<{ idOrSlug: string }>();
  const navigate = useNavigate();
  const { account, role } = useAuth();
  const domains = useDomains();
  const q = useGuide(idOrSlug);

  const guide = q.data ?? null;

  const canEdit = role === 'owner' || role === 'admin';
  const isOwnedByActiveAccount = guide && guide.accountId === account?.id;

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle');
  const [domainId, setDomainId] = useState('');
  const [bodyMd, setBodyMd] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (hydrated || !guide) return;
    setTitle(guide.title);
    setSlug(guide.slug);
    setDomainId(guide.domainId);
    setBodyMd(guide.bodyMd);
    setHydrated(true);
  }, [hydrated, guide]);

  const patch = usePatchGuide(guide?.id ?? '');
  const del = useDeleteGuide();

  const submitting = patch.isPending;
  const submitDisabled =
    submitting ||
    !title.trim() ||
    !slug ||
    !domainId ||
    bodyMd.length === 0 ||
    slugStatus === 'taken' ||
    slugStatus === 'checking';

  async function onSubmit(event?: React.FormEvent<HTMLFormElement>) {
    event?.preventDefault();
    setError(null);
    if (!guide) return;
    try {
      const updated = await patch.mutateAsync({
        slug,
        title: title.trim(),
        domainId,
        bodyMd,
      });
      navigate(`/guides/${updated.slug}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    }
  }

  if (q.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (q.isError || !guide) return <EmptyEditState reason="not-found" />;
  if (!canEdit) return <EmptyEditState reason="role" />;
  if (!isOwnedByActiveAccount) return <EmptyEditState reason="other-account" />;

  return (
    <>
      <form
        onSubmit={onSubmit}
        className="pb-[calc(13rem+env(safe-area-inset-bottom))] sm:pb-[calc(11rem+env(safe-area-inset-bottom))] md:pb-[calc(7.5rem+env(safe-area-inset-bottom))] lg:pb-8"
      >
        <header className="space-y-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            <Link to="/guides" className="hover:text-foreground">
              Guides
            </Link>{' '}
            · Edit
          </p>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
            Edit guide
          </h1>
          <p className="text-muted-foreground max-w-2xl">
            Updates replace the live guide immediately. There's no draft mode.
          </p>
        </header>

        <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
          <section className="space-y-5" aria-labelledby="meta">
            <h2
              id="meta"
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground"
            >
              Metadata
            </h2>

            <div className="space-y-1.5">
              <Label
                htmlFor="title-input"
                className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground"
              >
                Title
              </Label>
              <Input
                id="title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="h-12 text-base"
                data-testid="edit-title"
                required
              />
            </div>

            <SlugField
              source={title}
              value={slug}
              onChange={setSlug}
              onStatusChange={setSlugStatus}
              excludeId={guide.id}
              initialEdited
            />

            <div className="space-y-1.5">
              <Label
                htmlFor="domain-select"
                className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground"
              >
                Domain
              </Label>
              <select
                id="domain-select"
                value={domainId}
                onChange={(e) => setDomainId(e.target.value)}
                className="h-12 w-full rounded-md border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="edit-domain"
                required
              >
                <option value="">Pick a domain…</option>
                {(domains.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          </section>

          <section className="space-y-3" aria-labelledby="body">
            <h2
              id="body"
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground"
            >
              Body
            </h2>
            <div data-testid="edit-body">
              <MarkdownEditor
                value={bodyMd}
                onChange={setBodyMd}
                minHeight="60vh"
                toolbar="auto"
                maxLength={200_000}
                onSave={() => onSubmit()}
              />
            </div>
          </section>
        </div>

        {error ? (
          <div className="mt-6 rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        ) : null}

        <div
          className={
            'fixed inset-x-0 z-30 border-t border-rule bg-background/95 ' +
            'shadow-[0_-12px_24px_-12px_rgb(0_0_0_/_0.12)] backdrop-blur ' +
            'bottom-[calc(3.5rem+env(safe-area-inset-bottom))] ' +
            'md:bottom-0 md:pb-[env(safe-area-inset-bottom)] md:shadow-none ' +
            'lg:static lg:mt-8 lg:border-0 lg:bg-transparent lg:backdrop-blur-0 lg:pb-0 lg:shadow-none'
          }
        >
          <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-end lg:px-0">
            <div className="flex items-center gap-2 sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate('/guides')}
                className="h-12 px-3 text-muted-foreground hover:text-foreground"
                data-testid="edit-cancel"
              >
                <ChevronLeft className="mr-1 h-4 w-4" aria-hidden /> Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitDisabled}
                className="h-12 flex-1 sm:flex-initial sm:px-6"
                data-testid="edit-submit"
              >
                {submitting ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>
        </div>

        <section
          className="mt-12 lg:mt-8 rounded-md border border-destructive/30 bg-destructive/[0.03] p-4"
          aria-labelledby="danger-zone"
        >
          <h2
            id="danger-zone"
            className="font-mono text-[10px] uppercase tracking-[0.18em] text-destructive mb-2"
          >
            Danger zone
          </h2>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="font-display text-base">Delete this guide</p>
              <p className="text-sm text-muted-foreground">
                Permanently removes the guide from this account.
              </p>
              {deleteError ? (
                <p className="text-sm text-warning font-medium pt-2" role="alert">
                  {deleteError}
                </p>
              ) : null}
            </div>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setDeleteError(null);
                setDeleteOpen(true);
              }}
              className="shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
              data-testid="edit-delete"
            >
              <Trash2 className="h-4 w-4 mr-2" aria-hidden /> Delete
            </Button>
          </div>
        </section>
      </form>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete this guide?"
        description="The guide is permanently removed from this account. Anyone reading it loses access immediately."
        confirmLabel="Delete guide"
        busyLabel="Deleting…"
        tone="destructive"
        testId="confirm-delete-guide"
        onConfirm={async () => {
          if (!guide) return;
          try {
            await del.mutateAsync(guide.id);
            setDeleteOpen(false);
            navigate('/guides');
          } catch (err) {
            setDeleteError(
              err instanceof Error ? err.message : 'Could not delete guide',
            );
            setDeleteOpen(false);
          }
        }}
      />
    </>
  );
}

function EmptyEditState({ reason }: { reason: 'not-found' | 'role' | 'other-account' }) {
  const copy =
    reason === 'not-found'
      ? "We couldn't find that guide in your account."
      : reason === 'role'
        ? 'Editing guides is restricted to owners and admins on this account.'
        : "This guide is owned by a different account and can't be edited from here.";
  return (
    <div className="space-y-4 max-w-md">
      <h1 className="font-display text-2xl font-semibold tracking-tight">Can't edit</h1>
      <p className="text-muted-foreground">{copy}</p>
      <Link
        to="/guides"
        className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-wider text-primary hover:underline"
      >
        <ChevronLeft className="h-3.5 w-3.5" aria-hidden /> Back to guides
      </Link>
    </div>
  );
}
